import { BulletinResource } from "@rubi/api-generated";
import dayjs from "dayjs";
import { t } from "i18next";

import {
  IAlertProperties,
  IBulletinFlag,
  IBulletinResolution,
  IContact,
  ICreator,
  IEvidenceFile,
  IPdfFile,
  IPoliceService,
  ITag,
  PlaceType,
} from "@/types";

import {
  ALERT_CLASSIFICATION,
  BULLETIN_TYPE,
  LOCATION_CLASSIFICATION,
  LOCATION_CLASSIFICATION_REFERENCE,
  emptyContact,
} from "./constants";

export const getAddressClassification = (address) => {
  if (typeof address === "string") {
    return t(
      `bulletin/incident_location_type:${LOCATION_CLASSIFICATION_REFERENCE["Incident Address"]}`,
    );
  } else if (address === null) {
    return t(
      `bulletin/incident_location_type:${LOCATION_CLASSIFICATION_REFERENCE["Incident Address"]}`,
    );
  } else {
    if (address.location_classification === LOCATION_CLASSIFICATION.OTHER) {
      return address.other_location_classification;
    }
    return t(
      `bulletin/incident_location_type:${LOCATION_CLASSIFICATION_REFERENCE[address.location_classification]}`,
    );
  }
};

export const getAddressDescription = (address) => {
  if (typeof address === "string") {
    return address;
  } else if (address === null) {
    return [""];
  } else {
    return address.description;
  }
};

export function updateToContextStructure(
  alert: BulletinResource,
): IAlertProperties {
  const {
    issuing_police_service,
    type,
    associated_crime,
    occurrence_number,
    incident_times,
    description,
    location,
    tags,
    entities,
    issued_time,
    contacts,
    evidence_files,
    operational_area,
    approved_by,
    approved_by_badge,
    classification,
    id,
    creator,
    is_draft,
    pdf,
    resolutions,
    description_fr,
    original_language,
    flags,
  } = alert;
  const addresses: PlaceType[] =
    (location as { addresses?: PlaceType[] })?.addresses ?? [];

  return {
    policeService: (issuing_police_service as IPoliceService) ?? null,
    type: (type as BULLETIN_TYPE | null) ?? null,
    associatedCrimes: associated_crime.map((crime) => ({
      crime,
      group: "",
    })),
    occurrenceNumbers: occurrence_number.map((occurrence) => occurrence),
    incidentTimes:
      incident_times.length > 0
        ? incident_times
        : [{ time_type: "exact", start_date: null, end_date: null }],
    description,
    descriptionFr: description_fr ?? null,
    originalLanguage: original_language ?? null,
    rawAddresses: addresses || [null],
    tagDict: tags as { [key: string]: ITag },
    // Typed all the way through, unlike the JSON columns beside it: `tags`, `location`, `pdf` and
    // `evidence_files` are all `{ [key: string]: unknown }` on the wire because FastAPI has no
    // model for them, while entities are a table with a discriminated union over it.
    entities: entities ?? [],
    flags: (flags ?? []) as unknown as IBulletinFlag[],
    issuedTime: issued_time ? dayjs(issued_time) : null,
    bulletinId: id,
    operationalArea: operational_area ?? null,
    approvedBy: approved_by ?? null,
    approvedByBadge: approved_by_badge ?? null,
    classification:
      (classification as ALERT_CLASSIFICATION) ??
      ALERT_CLASSIFICATION.PROTECTED_A,
    contacts: contacts as IContact[],
    creator: creator as ICreator | undefined,
    evidence_files: evidence_files as { [key: string]: IEvidenceFile },
    isDraft: is_draft === 0 ? false : true,
    pdf: pdf as IPdfFile | Record<string, never>,
    resolutions: (resolutions ?? []) as IBulletinResolution[],
  };
}

export const isContactEmpty = (contact: IContact) => {
  return Object.keys(contact).every((key) => {
    return contact[key] === emptyContact[key];
  });
};

export const validateOperationalArea = (
  policeService: IPoliceService,
  operationalArea,
) => {
  const areas = policeService.rubi_org?.operational_areas;
  if (areas && areas.length > 0) {
    return operationalArea ? false : true;
  }
  return false;
};

export const validateContactName = (contacts: IContact[]) => {
  const contactNameErrors = contacts.map((contact) => {
    return contact.name === "" ? true : false;
  });
  if (contacts.length > 1) {
    const lastContact = contacts[contacts.length - 1];
    if (isContactEmpty(lastContact)) {
      contactNameErrors[contactNameErrors.length - 1] = false;
    }
  }
  return contactNameErrors;
};

export const validateGeocodedAddresses = async (addresses) => {
  if (!window.google?.maps?.Geocoder) {
    console.error("Google Maps API is not loaded yet");
    return [];
  }
  const geocoder = new google.maps.Geocoder();
  const addressValidities = addresses.map(async (address) => {
    const hasLocationType = () => {
      const locationClassifications = Object.values(LOCATION_CLASSIFICATION);
      if (locationClassifications.includes(address?.location_classification)) {
        if (
          address.location_classification === LOCATION_CLASSIFICATION.OTHER &&
          address.other_location_classification &&
          address.other_location_classification !== ""
        ) {
          return true;
        } else if (
          address.location_classification !== LOCATION_CLASSIFICATION.OTHER
        ) {
          return true;
        } else {
          return false;
        }
      }
      return false;
    };
    return new Promise<{
      address: PlaceType | string | null;
      isValid: boolean;
      hasLocationType: boolean;
    }>((resolve) => {
      if (typeof address === "string") {
        resolve({ address: address, isValid: false, hasLocationType: false });
      }
      if (address?.place_id) {
        geocoder.geocode({ placeId: address.place_id }, (results, status) => {
          if (status === "OK") {
            resolve({
              address: address,
              isValid: true,
              hasLocationType: hasLocationType(),
            });
          } else {
            resolve({
              address: address,
              isValid: false,
              hasLocationType: hasLocationType(),
            });
          }
        });
      } else {
        resolve({
          address: address,
          isValid: false,
          hasLocationType: hasLocationType(),
        });
      }
    });
  });

  const results = await Promise.all(addressValidities);
  return results;
};

// utility functions for localStorage with expiry
export const setLocalStorageWithExpiry = <T>(
  key: string,
  value: T,
  ttl: number,
) => {
  const now = new Date();
  const item = {
    value,
    expiry: now.getTime() + ttl,
  };
  localStorage.setItem(key, JSON.stringify(item));
};

// get data from localStorage with expiry
export const getLocalStorageWithExpiry = (key: string) => {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) {
    return null;
  }
  try {
    const item = JSON.parse(itemStr);
    const now = new Date();
    if (now.getTime() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  } catch {
    return null;
  }
};
